import { isString } from 'vega-util';
import { isBinning } from '../bin';
import { channelDefType, isFieldDef, isFieldOrDatumDefForTimeFormat, isPositionFieldOrDatumDef, isScaleFieldDef, vgField } from '../channeldef';
import { fieldValidPredicate } from '../predicate';
import { ScaleType } from '../scale';
import { formatExpression, normalizeTimeUnit, timeUnitSpecifierExpression } from '../timeunit';
import { QUANTITATIVE } from '../type';
import { stringify } from '../util';
import { isSignalRef } from '../vega.schema';
import { datumDefToExpr } from './mark/encode/valueref';
export function isCustomFormatType(formatType) {
  return formatType && formatType !== 'number' && formatType !== 'time';
}
function customFormatExpr(formatType, field, format) {
  return "".concat(formatType, "(").concat(field).concat(format ? ", ".concat(stringify(format)) : '', ")");
}
export var BIN_RANGE_DELIMITER = " \u2013 ";
export function formatSignalRef(_ref) {
  var fieldOrDatumDef = _ref.fieldOrDatumDef,
    format = _ref.format,
    formatType = _ref.formatType,
    expr = _ref.expr,
    normalizeStack = _ref.normalizeStack,
    config = _ref.config;
  if (isCustomFormatType(formatType)) {
    return formatCustomType({
      fieldOrDatumDef: fieldOrDatumDef,
      format: format,
      formatType: formatType,
      expr: expr,
      config: config
    });
  }
  var field = fieldToFormat(fieldOrDatumDef, expr, normalizeStack);
  var type = channelDefType(fieldOrDatumDef);
  if (format === undefined && formatType === undefined && config.customFormatTypes) {
    if (type === 'quantitative') {
      if (normalizeStack && config.normalizedNumberFormatType) return formatCustomType({
        fieldOrDatumDef: fieldOrDatumDef,
        format: config.normalizedNumberFormat,
        formatType: config.normalizedNumberFormatType,
        expr: expr,
        config: config
      });
      if (config.numberFormatType) {
        return formatCustomType({
          fieldOrDatumDef: fieldOrDatumDef,
          format: config.numberFormat,
          formatType: config.numberFormatType,
          expr: expr,
          config: config
        });
      }
    }
    if (type === 'temporal' && config.timeFormatType && isFieldDef(fieldOrDatumDef) && fieldOrDatumDef.timeUnit === undefined) {
      return formatCustomType({
        fieldOrDatumDef: fieldOrDatumDef,
        format: config.timeFormat,
        formatType: config.timeFormatType,
        expr: expr,
        config: config
      });
    }
  }
  if (isFieldOrDatumDefForTimeFormat(fieldOrDatumDef)) {
    var _normalizeTimeUnit, _fieldOrDatumDef$scal;
    var signal = timeFormatExpression({
      field: field,
      timeUnit: isFieldDef(fieldOrDatumDef) ? (_normalizeTimeUnit = normalizeTimeUnit(fieldOrDatumDef.timeUnit)) === null || _normalizeTimeUnit === void 0 ? void 0 : _normalizeTimeUnit.unit : undefined,
      format: format,
      formatType: config.timeFormatType,
      rawTimeFormat: config.timeFormat,
      isUTCScale: isScaleFieldDef(fieldOrDatumDef) && ((_fieldOrDatumDef$scal = fieldOrDatumDef.scale) === null || _fieldOrDatumDef$scal === void 0 ? void 0 : _fieldOrDatumDef$scal.type) === ScaleType.UTC
    });
    return signal ? {
      signal: signal
    } : undefined;
  }
  format = numberFormat({
    type: type,
    specifiedFormat: format,
    config: config,
    normalizeStack: normalizeStack
  });
  if (isFieldDef(fieldOrDatumDef) && isBinning(fieldOrDatumDef.bin)) {
    var endField = vgField(fieldOrDatumDef, {
      expr: expr,
      binSuffix: 'end'
    });
    return {
      signal: binFormatExpression(field, endField, format, formatType, config)
    };
  } else if (format || channelDefType(fieldOrDatumDef) === 'quantitative') {
    return {
      signal: "".concat(formatExpr(field, format))
    };
  } else {
    return {
      signal: "isValid(".concat(field, ") ? ").concat(field, " : \"\"+").concat(field)
    };
  }
}
function fieldToFormat(fieldOrDatumDef, expr, normalizeStack) {
  if (isFieldDef(fieldOrDatumDef)) {
    if (normalizeStack) {
      return "".concat(vgField(fieldOrDatumDef, {
        expr: expr,
        suffix: 'end'
      }), "-").concat(vgField(fieldOrDatumDef, {
        expr: expr,
        suffix: 'start'
      }));
    } else {
      return vgField(fieldOrDatumDef, {
        expr: expr
      });
    }
  } else {
    return datumDefToExpr(fieldOrDatumDef);
  }
}
export function formatCustomType(_ref2) {
  var fieldOrDatumDef = _ref2.fieldOrDatumDef,
    format = _ref2.format,
    formatType = _ref2.formatType,
    expr = _ref2.expr,
    normalizeStack = _ref2.normalizeStack,
    config = _ref2.config,
    field = _ref2.field;
  field !== null && field !== void 0 ? field : field = fieldToFormat(fieldOrDatumDef, expr, normalizeStack);
  if (field !== 'datum.value' &&
  // For axis/legend, we can't correctly know the end of the bin from `datum`
  isFieldDef(fieldOrDatumDef) && isBinning(fieldOrDatumDef.bin)) {
    var endField = vgField(fieldOrDatumDef, {
      expr: expr,
      binSuffix: 'end'
    });
    return {
      signal: binFormatExpression(field, endField, format, formatType, config)
    };
  }
  return {
    signal: customFormatExpr(formatType, field, format)
  };
}
export function guideFormat(fieldOrDatumDef, type, format, formatType, config, omitTimeFormatConfig // axis doesn't use config.timeFormat
) {
  if (isString(formatType) && isCustomFormatType(formatType)) {
    return undefined; // handled in encode block
  } else if (format === undefined && formatType === undefined && config.customFormatTypes) {
    if (channelDefType(fieldOrDatumDef) === 'quantitative') {
      if (config.normalizedNumberFormatType && isPositionFieldOrDatumDef(fieldOrDatumDef) && fieldOrDatumDef.stack === 'normalize') {
        return undefined; // handled in encode block
      }
      if (config.numberFormatType) {
        return undefined; // handled in encode block
      }
    }
  }
  if (isPositionFieldOrDatumDef(fieldOrDatumDef) && fieldOrDatumDef.stack === 'normalize' && config.normalizedNumberFormat) {
    return numberFormat({
      type: 'quantitative',
      config: config,
      normalizeStack: true
    });
  }
  if (isFieldOrDatumDefForTimeFormat(fieldOrDatumDef)) {
    var _normalizeTimeUnit2;
    var timeUnit = isFieldDef(fieldOrDatumDef) ? (_normalizeTimeUnit2 = normalizeTimeUnit(fieldOrDatumDef.timeUnit)) === null || _normalizeTimeUnit2 === void 0 ? void 0 : _normalizeTimeUnit2.unit : undefined;
    if (timeUnit === undefined && config.customFormatTypes && config.timeFormatType) {
      return undefined; // hanlded in encode block
    }
    return timeFormat({
      specifiedFormat: format,
      timeUnit: timeUnit,
      config: config,
      omitTimeFormatConfig: omitTimeFormatConfig
    });
  }
  return numberFormat({
    type: type,
    specifiedFormat: format,
    config: config
  });
}
export function guideFormatType(formatType, fieldOrDatumDef, scaleType) {
  if (formatType && (isSignalRef(formatType) || formatType === 'number' || formatType === 'time')) {
    return formatType;
  }
  if (isFieldOrDatumDefForTimeFormat(fieldOrDatumDef) && scaleType !== 'time' && scaleType !== 'utc') {
    var _normalizeTimeUnit3;
    return isFieldDef(fieldOrDatumDef) && (_normalizeTimeUnit3 = normalizeTimeUnit(fieldOrDatumDef === null || fieldOrDatumDef === void 0 ? void 0 : fieldOrDatumDef.timeUnit)) !== null && _normalizeTimeUnit3 !== void 0 && _normalizeTimeUnit3.utc ? 'utc' : 'time';
  }
  return undefined;
}
/**
 * Returns number format for a fieldDef.
 */
export function numberFormat(_ref3) {
  var type = _ref3.type,
    specifiedFormat = _ref3.specifiedFormat,
    config = _ref3.config,
    normalizeStack = _ref3.normalizeStack;
  // Specified format in axis/legend has higher precedence than fieldDef.format
  if (isString(specifiedFormat)) {
    return specifiedFormat;
  }
  if (type === QUANTITATIVE) {
    // we only apply the default if the field is quantitative
    return normalizeStack ? config.normalizedNumberFormat : config.numberFormat;
  }
  return undefined;
}
/**
 * Returns time format for a fieldDef for use in guides.
 */
export function timeFormat(_ref4) {
  var specifiedFormat = _ref4.specifiedFormat,
    timeUnit = _ref4.timeUnit,
    config = _ref4.config,
    omitTimeFormatConfig = _ref4.omitTimeFormatConfig;
  if (specifiedFormat) {
    return specifiedFormat;
  }
  if (timeUnit) {
    return {
      signal: timeUnitSpecifierExpression(timeUnit)
    };
  }
  return omitTimeFormatConfig ? undefined : config.timeFormat;
}
function formatExpr(field, format) {
  return "format(".concat(field, ", \"").concat(format || '', "\")");
}
function binNumberFormatExpr(field, format, formatType, config) {
  var _ref5;
  if (isCustomFormatType(formatType)) {
    return customFormatExpr(formatType, field, format);
  }
  return formatExpr(field, (_ref5 = isString(format) ? format : undefined) !== null && _ref5 !== void 0 ? _ref5 : config.numberFormat);
}
export function binFormatExpression(startField, endField, format, formatType, config) {
  if (format === undefined && formatType === undefined && config.customFormatTypes && config.numberFormatType) {
    return binFormatExpression(startField, endField, config.numberFormat, config.numberFormatType, config);
  }
  var start = binNumberFormatExpr(startField, format, formatType, config);
  var end = binNumberFormatExpr(endField, format, formatType, config);
  return "".concat(fieldValidPredicate(startField, false), " ? \"null\" : ").concat(start, " + \"").concat(BIN_RANGE_DELIMITER, "\" + ").concat(end);
}
/**
 * Returns the time expression used for axis/legend labels or text mark for a temporal field
 */
export function timeFormatExpression(_ref6) {
  var field = _ref6.field,
    timeUnit = _ref6.timeUnit,
    format = _ref6.format,
    formatType = _ref6.formatType,
    rawTimeFormat = _ref6.rawTimeFormat,
    isUTCScale = _ref6.isUTCScale;
  if (!timeUnit || format) {
    // If there is no time unit, or if user explicitly specifies format for axis/legend/text.
    if (!timeUnit && formatType) {
      return "".concat(formatType, "(").concat(field, ", '").concat(format, "')");
    }
    format = isString(format) ? format : rawTimeFormat; // only use provided timeFormat if there is no timeUnit.
    return "".concat(isUTCScale ? 'utc' : 'time', "Format(").concat(field, ", '").concat(format, "')");
  } else {
    return formatExpression(timeUnit, field, isUTCScale);
  }
}